// Domain-wide recipients.
//
// A recipient entry may be a single address ("jane@hushcrib.com") or a
// wildcard for a whole company domain ("*@hushcrib.com"), which admits every
// address at that domain. The provisioning skill adds the wildcard for the
// supplier's own domain (from Pipedrive) so any employee of the company can
// sign in without being listed one by one.
//
// Wildcards are refused for public mailbox providers: "*@gmail.com" would
// admit the whole world. PUBLIC_EMAIL_DOMAINS is the denylist the schema and
// the auth gate both enforce; the hub's edit-deal-room-recipients skill
// checks the same list before proposing a wildcard. Keep it lowercase.

export const PUBLIC_EMAIL_DOMAINS: ReadonlySet<string> = new Set([
    // Google
    "gmail.com", "googlemail.com",
    // Microsoft
    "outlook.com", "hotmail.com", "live.com", "msn.com", "hotmail.co.uk", "outlook.co.uk",
    "hotmail.fr", "live.co.uk", "passport.com",
    // Yahoo / AOL / Verizon family
    "yahoo.com", "yahoo.co.uk", "yahoo.ca", "yahoo.fr", "yahoo.de", "ymail.com", "rocketmail.com",
    "aol.com", "aim.com", "verizon.net",
    // Apple
    "icloud.com", "me.com", "mac.com",
    // Privacy / other consumer providers
    "protonmail.com", "proton.me", "pm.me", "tutanota.com", "tuta.io", "zoho.com", "zohomail.com",
    "gmx.com", "gmx.de", "gmx.net", "mail.com", "email.com", "fastmail.com", "hey.com",
    "yandex.com", "yandex.ru", "mail.ru", "qq.com", "163.com", "126.com", "sina.com",
    // US ISPs
    "comcast.net", "att.net", "sbcglobal.net", "bellsouth.net", "cox.net", "charter.net",
    "earthlink.net", "optonline.net", "frontier.com", "windstream.net", "centurylink.net",
    // Disposable / catch-alls people type into forms
    "example.com", "test.com", "mailinator.com", "guerrillamail.com", "10minutemail.com",
]);

const WILDCARD = /^\*@([a-z0-9-]+(?:\.[a-z0-9-]+)+)$/;

export function isWildcardRecipient(email: string): boolean {
    return WILDCARD.test(email.trim().toLowerCase());
}

export function wildcardDomain(email: string): string | null {
    const m = WILDCARD.exec(email.trim().toLowerCase());
    return m ? m[1] : null;
}

export function isPublicEmailDomain(domain: string): boolean {
    return PUBLIC_EMAIL_DOMAINS.has(domain.trim().toLowerCase());
}

// A wildcard entry is valid when it is well-formed AND not a public provider.
export function isAllowedWildcard(email: string): boolean {
    const d = wildcardDomain(email);
    return d !== null && !isPublicEmailDomain(d);
}

export function emailDomain(email: string): string {
    const at = email.lastIndexOf("@");
    return at === -1 ? "" : email.slice(at + 1).trim().toLowerCase();
}

// The signer: the supplier's lead contact, who defaults onto every agreement
// sent for signature (2026-10-27, Elijah). Only a named person can sign, never
// a company wildcard and never Bison staff. A room may store its pick as
// `signerEmail`; with none stored, or once that person leaves the list, the
// signer is the first named non-Bison recipient.
export function signerCandidates<T extends { email: string }>(recipients: T[]): T[] {
    return recipients.filter((r) => !isWildcardRecipient(r.email) && !r.email.toLowerCase().endsWith("@bisoncommerce.com"));
}

export function roomSigner<T extends { email: string }>(recipients: T[], signerEmail?: string): T | undefined {
    const candidates = signerCandidates(recipients);
    const stored = signerEmail?.toLowerCase();
    return candidates.find((r) => r.email.toLowerCase() === stored) ?? candidates[0];
}
